import { dayGreetingText } from './dayGreeting.js';
import { parseMascotEconomy } from './outfitCardWords.js';
/** The built-in Gyro's look id. */
export const BUILT_IN_MASCOT_LOOK = 'default';
/** The group the built-in Gyro and every outfit without a category are shown in (outfit categories, owner 2026-10-09). */
export const GENERAL_GROUP_ID = 'general';
export const GENERAL_GROUP_NAME = 'General';
/** A group with more outfits than this starts collapsed, until the person opens it (then their choice is remembered). */
export const MASCOT_GROUP_COLLAPSE_OVER = 12;
/** Where this browser remembers which groups the person collapsed or opened (localStorage; per person on a desktop). */
export const MASCOT_GROUPS_STORAGE_KEY = 'gyro:outfit-groups:collapsed';
/**
 * Dispatched on `window` after a look is saved. The desktop shell relays it to the agent, which re-reads the pick
 * through device custody; the event carries nothing and is never trusted as the pick.
 */
export const MASCOT_LOOK_CHANGED_EVENT = 'gyro:mascot-look-changed';
/** gyro-svc's per-turn read tool that lists the person's looks on a desktop turn ("change your outfit"). */
export const MASCOT_LOOK_LIST_TOOL = 'gyro_list_looks';
/**
 * Whether a turn's tool result puts the outfit picker under its reply: that exact tool, and only when it succeeded.
 * The result itself never reaches the widget (a `tool_result` frame is the tool name and ok); the picker loads the
 * gallery on its own, through the same forwarded routes as Settings.
 */
export function marksLookPicker(tool, ok) {
    return ok && tool === MASCOT_LOOK_LIST_TOOL;
}
const OUTFIT_ID = /^o-[a-z0-9]{10}$/;
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
/** gyro-svc caps a thumbnail at 32 KB of PNG, which is 43,692 characters of base64. */
const THUMBNAIL_MAX_BASE64 = 43_692;
const NAME_MAX = 40;
const asOutfitId = (value) => typeof value === 'string' && OUTFIT_ID.test(value) ? value : undefined;
const asLook = (value) => value === BUILT_IN_MASCOT_LOOK ? value : asOutfitId(value);
const isDimension = (value) => typeof value === 'number' && Number.isInteger(value) && value > 0;
/** gyro-svc's outfit id: `o-` and 10 lower-case letters or digits (the built-in Gyro, `'default'`, is not one). */
export function isMascotOutfitId(value) {
    return asOutfitId(value) !== undefined;
}
/** A rating's stars: a whole number from 1 to 5. */
export function isMascotStars(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
}
/** The person's own stars as gyro-svc sends them: 1 to 5, or null; undefined for anything else. */
function parseMine(value) {
    return value === null ? null : isMascotStars(value) ? value : undefined;
}
/** Everyone's average and count: null and 0 while nobody rated it, else an average from 1 to 5 (one decimal) and a count. */
function parseStats(average, count) {
    if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0)
        return undefined;
    if (count === 0)
        return average === null ? { average: null, count } : undefined;
    if (typeof average !== 'number' || !Number.isFinite(average) || average < 1 || average > 5)
        return undefined;
    return { average: Math.round(average * 10) / 10, count };
}
/**
 * An outfit's `rating` in a LooksView (owner, 2026-10-06), or undefined when it is absent or malformed: that outfit then
 * shows no stars, and the rest of it, and of the gallery, stays.
 */
export function parseMascotRating(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { mine, average, count } = value;
    const own = parseMine(mine);
    const stats = parseStats(average, count);
    return own === undefined || !stats ? undefined : { mine: own, ...stats };
}
/**
 * The answer to a rating of `outfitId` (PUT or DELETE /v1/mascot/outfits/:id/rating), or null when it is not one: the
 * person's stars, with everyone's average and count when gyro-svc could read them (`{ mine }` alone otherwise).
 */
export function parseMascotRatingAnswer(value, outfitId) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { outfitId: id, rating } = value;
    if (id !== outfitId || !isMascotOutfitId(id) || !rating || typeof rating !== 'object' || Array.isArray(rating))
        return null;
    const fields = rating;
    const mine = parseMine(fields.mine);
    if (mine === undefined)
        return null;
    if (!('average' in fields) && !('count' in fields))
        return { outfitId: id, rating: { mine } };
    const stats = parseStats(fields.average, fields.count);
    return stats ? { outfitId: id, rating: { mine, ...stats } } : null;
}
function parseWindow(value) {
    if (!value || typeof value !== 'object')
        return null;
    const { kind, startMs, endMs } = value;
    if (kind === 'always')
        return { kind: 'always' };
    if (kind === 'between' && typeof startMs === 'number' && typeof endMs === 'number' && Number.isFinite(startMs) && Number.isFinite(endMs) && startMs < endMs) {
        return { kind: 'between', startMs, endMs };
    }
    return null;
}
const CATEGORY_ID = /^[a-z0-9][a-z0-9-]{0,31}$/;
/**
 * An outfit's `category` in a LooksView (outfit categories, owner 2026-10-09), or undefined when it is absent or
 * malformed: that outfit is then shown under General, and the rest of it stays.
 */
export function parseMascotCategory(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { id, name } = value;
    const label = typeof name === 'string' ? name.trim() : '';
    if (typeof id !== 'string' || !CATEGORY_ID.test(id) || id === GENERAL_GROUP_ID || !label || label.length > NAME_MAX || /[\u0000-\u001f\u007f]/.test(label))
        return undefined;
    return { id, name: label };
}
/** A thumbnail as gyro-svc sends it inline: base64 PNG bytes, at most 32 KB of them. */
const isThumbnailBase64 = (value) => typeof value === 'string' && value.length > 0 && value.length <= THUMBNAIL_MAX_BASE64 && BASE64.test(value);
/** The person thumbnail route for an outfit (the 2,000 gallery): exactly this path, with this outfit's id. */
export function mascotThumbnailPath(outfitId) {
    return `/v1/mascot/outfits/${outfitId}/thumbnail`;
}
/** A count or a timestamp as gyro-svc sends it: a safe integer at or above `min`; undefined for anything else (the outfit then has none). */
function parseCount(value, min) {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= min ? value : undefined;
}
function parseOutfit(value) {
    if (!value || typeof value !== 'object')
        return null;
    const { id, name, width, height, sha256, thumbnailPng, thumbnailUrl, window: rawWindow, featured, rating: rawRating, category: rawCategory, addedAtMs: rawAdded, picks: rawPicks, dayOutfit, hasActions, } = value;
    const outfitId = asOutfitId(id);
    const label = typeof name === 'string' ? name.trim() : '';
    const galleryWindow = parseWindow(rawWindow);
    if (!outfitId || !label || label.length > NAME_MAX || /[\u0000-\u001f\u007f]/.test(label)
        || !isDimension(width) || !isDimension(height)
        || typeof sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(sha256)
        || !galleryWindow || typeof featured !== 'boolean')
        return null;
    // The thumbnail: inline (every gyro-svc before the 2,000 gallery, and any answer not asked for URLs), or by URL, which
    // must be the person thumbnail route for this very outfit (never an address of the server's choosing); one of the two.
    const inline = thumbnailPng === undefined ? undefined : isThumbnailBase64(thumbnailPng) ? thumbnailPng : null;
    const byUrl = thumbnailUrl === undefined ? undefined : thumbnailUrl === mascotThumbnailPath(outfitId) ? thumbnailUrl : null;
    if (inline === null || byUrl === null || (inline === undefined && byUrl === undefined))
        return null;
    const rating = parseMascotRating(rawRating);
    const category = parseMascotCategory(rawCategory);
    // For the gallery's sort and filters (0.16.8): each absent, or malformed, means the outfit has none; the rest stays.
    const addedAtMs = parseCount(rawAdded, 1);
    const picks = parseCount(rawPicks, 0);
    // The outfit economy's fields (rarity, serial, copies, supply, sold out…): folded into one object; absent (an older
    // gyro-svc, the economy off) or malformed means no crystal on this outfit, and the rest stays.
    const economy = parseMascotEconomy(value);
    return {
        id: outfitId, name: label, width, height, sha256, window: galleryWindow, featured,
        ...(inline !== undefined ? { thumbnailPng: inline } : {}),
        ...(byUrl !== undefined ? { thumbnailUrl: byUrl } : {}),
        ...(rating ? { rating } : {}),
        ...(category ? { category } : {}),
        ...(addedAtMs !== undefined ? { addedAtMs } : {}),
        ...(picks !== undefined ? { picks } : {}),
        ...(dayOutfit === true ? { dayOutfit: true } : {}),
        // Kept only as a boolean: absent (an older gyro-svc, or mascot animations off) shows no icon, and so does anything else.
        ...(typeof hasActions === 'boolean' ? { hasActions } : {}),
        ...(economy ? { economy } : {}),
    };
}
/** How many thumbnails one GET /v1/mascot/thumbnails asks for at most (gyro-svc MASCOT_THUMBNAILS_BATCH_MAX). */
export const MASCOT_THUMBNAILS_BATCH_MAX = 32;
/**
 * The thumbnails batch route's answer for `ids`: what was read (by id, only ids that were asked for, each once, with its
 * image hash), which ids are not in the gallery, and which could not be read now. An entry that is malformed, or names
 * an id that was not asked for, counts as failed (read again later) rather than refusing the batch; an answer that is
 * not a batch at all is null.
 */
export function parseMascotThumbnailBatch(value, ids) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { thumbnails: rawThumbnails, missing: rawMissing, failed: rawFailed } = value;
    if (!Array.isArray(rawThumbnails) || !Array.isArray(rawMissing) || !Array.isArray(rawFailed))
        return null;
    const asked = new Set(ids);
    const thumbnails = {};
    for (const entry of rawThumbnails) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry))
            continue;
        const { id, sha256, thumbnailPng } = entry;
        if (typeof id !== 'string' || !asked.has(id) || thumbnails[id] || typeof sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(sha256) || !isThumbnailBase64(thumbnailPng))
            continue;
        thumbnails[id] = { sha256, thumbnailPng };
    }
    const missing = rawMissing.filter((id) => typeof id === 'string' && asked.has(id) && !thumbnails[id]);
    const answered = new Set([...Object.keys(thumbnails), ...missing]);
    // Whatever was asked for and neither read nor missing is failed, whether or not gyro-svc listed it.
    const failed = ids.filter((id) => !answered.has(id));
    return { thumbnails, missing, failed };
}
/** The largest image gyro-svc stores (768,000 bytes of PNG), as base64: 1,024,000 characters. */
export const MASCOT_IMAGE_MAX_BASE64 = 1_024_000;
/** The person image route's answer for `outfitId`: its full PNG (base64) with its pixel size and hash; null when the answer is not that. */
export function parseMascotImage(value, outfitId) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { id, sha256, width, height, imagePng } = value;
    if (id !== outfitId || typeof sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(sha256) || !isDimension(width) || !isDimension(height)
        || typeof imagePng !== 'string' || imagePng.length === 0 || imagePng.length > MASCOT_IMAGE_MAX_BASE64 || !BASE64.test(imagePng))
        return null;
    return { sha256, width, height, imagePng };
}
/** The largest animation preview sheet gyro-svc makes (6,000,000 bytes of PNG, desktopMascotActionPreview.ts; frames of 256 px since 2026-10-21), as base64. */
export const MASCOT_ACTIONS_PREVIEW_MAX_BASE64 = 8_000_000;
/**
 * The person animation-preview route's answer for `outfitId` (GET /v1/mascot/outfits/:id/actions-preview, 0.17.3): the sprite
 * sheet (base64) with its frame count, fps, frame size and columns, each a sane number; null when the answer is not that.
 */
export function parseMascotActionsPreview(value, outfitId) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { id, packId, fps, frameCount, frameWidth, frameHeight, columns, sheetPng } = value;
    const count = (raw, max) => Number.isInteger(raw) && raw >= 1 && raw <= max;
    if (id !== outfitId || typeof packId !== 'string' || !/^ap-[a-f0-9]{16}$/.test(packId) || typeof fps !== 'number' || !(fps > 0 && fps <= 60)
        || !count(frameCount, 240) || !count(frameWidth, 1024) || !count(frameHeight, 1024) || !count(columns, 16) || columns > frameCount
        || typeof sheetPng !== 'string' || sheetPng.length === 0 || sheetPng.length > MASCOT_ACTIONS_PREVIEW_MAX_BASE64 || !BASE64.test(sheetPng))
        return null;
    return { packId, fps, frameCount, frameWidth, frameHeight, columns, sheetPng };
}
/** The person thumbnail route's answer for `outfitId`: its thumbnail, base64; null when the answer is not that. */
export function parseMascotThumbnail(value, outfitId) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { id, thumbnailPng } = value;
    return id === outfitId && isThumbnailBase64(thumbnailPng) ? thumbnailPng : null;
}
/** What a tile shows while its thumbnail is on its way (a URL-mode view): a transparent pixel, so the tile keeps its size and name. */
export const MASCOT_TILE_PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
/** Today's day outfit in a LooksView (day outfits), or null when it is not well-formed: the gallery still parses. */
function parseDay(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { outfitId, name, greeting, startMs, endMs } = value;
    const id = asOutfitId(outfitId);
    const label = typeof name === 'string' ? name.trim() : '';
    const text = dayGreetingText(greeting);
    if (!id || !label || label.length > NAME_MAX || /[\u0000-\u001f\u007f]/.test(label) || text === null
        || typeof startMs !== 'number' || typeof endMs !== 'number' || !Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs)
        return null;
    return { outfitId: id, name: label, greeting: text, startMs, endMs };
}
const CHANGE_TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/**
 * The person's outfit loop in a LooksView (outfit rotation, owner 2026-10-19), or undefined when it is malformed: the
 * gallery then offers no rotation controls, and the rest of it stays. Null stays null (the service offers rotation and
 * the person has no loop).
 */
export function parseMascotRotation(value) {
    if (value === null)
        return null;
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { outfitIds, every, unit, changeTime, timeZone, order, startedAtMs, paused, pausedAtMs, pausedPickId, nowId, nextChangeMs, available } = value;
    if (!Array.isArray(outfitIds) || outfitIds.length < 2 || !outfitIds.every(isMascotOutfitId) || new Set(outfitIds).size !== outfitIds.length)
        return undefined;
    if ((unit !== 'hours' && unit !== 'days') || typeof every !== 'number' || !Number.isInteger(every) || every < 1)
        return undefined;
    if (order !== 'picked' && order !== 'random')
        return undefined;
    if (unit === 'days' ? (typeof changeTime !== 'string' || !CHANGE_TIME.test(changeTime) || typeof timeZone !== 'string' || !timeZone) : (changeTime !== null || timeZone !== null))
        return undefined;
    if (parseCount(startedAtMs, 1) === undefined || parseCount(nextChangeMs, 1) === undefined || typeof paused !== 'boolean')
        return undefined;
    if (pausedAtMs !== null && parseCount(pausedAtMs, 1) === undefined)
        return undefined;
    if (pausedPickId !== null && asLook(pausedPickId) === undefined)
        return undefined;
    if (nowId !== null && (typeof nowId !== 'string' || !outfitIds.includes(nowId)))
        return undefined;
    if (!Array.isArray(available) || !available.every((id) => typeof id === 'string' && outfitIds.includes(id)))
        return undefined;
    return {
        outfitIds: outfitIds, every, unit, changeTime: changeTime, timeZone: timeZone, order, startedAtMs: startedAtMs,
        paused, pausedAtMs: pausedAtMs, pausedPickId: pausedPickId, nowId: nowId, nextChangeMs: nextChangeMs, available: available,
    };
}
/**
 * gyro-svc's LooksView, or null when the answer is not one. An outfit that is malformed (or repeats an id) is dropped
 * rather than failing the whole gallery: the built-in Gyro and the other outfits stay pickable. So is a malformed `day`;
 * `pickHeld` is kept only as a boolean; a malformed `rotation` counts as none offered.
 */
export function parseMascotLooksView(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    const pick = record.pick === null ? null : asLook(record.pick);
    const showing = asLook(record.showing);
    const featuredId = record.featuredId === null ? null : asOutfitId(record.featuredId);
    if (pick === undefined || showing === undefined || featuredId === undefined || !Array.isArray(record.outfits))
        return null;
    const outfits = [];
    for (const entry of record.outfits) {
        const outfit = parseOutfit(entry);
        if (outfit && !outfits.some((known) => known.id === outfit.id))
            outfits.push(outfit);
    }
    const day = parseDay(record.day);
    const rotation = 'rotation' in record ? parseMascotRotation(record.rotation) : undefined;
    return {
        pick, showing, featuredId, outfits,
        ...(day ? { day } : {}),
        ...(typeof record.pickHeld === 'boolean' ? { pickHeld: record.pickHeld } : {}),
        ...(typeof record.builtInHasActions === 'boolean' ? { builtInHasActions: record.builtInHasActions } : {}),
        ...(rotation !== undefined ? { rotation } : {}),
    };
}
/**
 * An outfit's window end in words: "Until Nov 1" ("Until Jan 3, 2027" in another year), or '' for an outfit that is
 * always in the gallery. The end is exclusive, so the outfit leaves the gallery at that moment.
 */
export function mascotWindowEnd(galleryWindow, options = {}) {
    if (galleryWindow.kind !== 'between')
        return '';
    const { locale, timeZone, now = Date.now() } = options;
    const year = new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone });
    const otherYear = year.format(galleryWindow.endMs) !== year.format(now);
    const day = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', ...(otherYear ? { year: 'numeric' } : {}), timeZone });
    return `Until ${day.format(galleryWindow.endMs)}`;
}
/**
 * The tiles in their groups: General first (the built-in Gyro and every outfit without a category), then each category
 * by name (A to Z, then by id), each in catalog order. Empty while no outfit has a category: the gallery is then one flat
 * list, exactly as before categories.
 */
export function mascotLookGroups(tiles) {
    if (!tiles.some((tile) => tile.category))
        return [];
    const general = { id: GENERAL_GROUP_ID, name: GENERAL_GROUP_NAME, tiles: [] };
    const groups = new Map();
    for (const tile of tiles) {
        if (!tile.category) {
            general.tiles.push(tile);
            continue;
        }
        let group = groups.get(tile.category.id);
        if (!group) {
            group = { id: tile.category.id, name: tile.category.name, tiles: [] };
            groups.set(group.id, group);
        }
        group.tiles.push(tile);
    }
    const named = [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id));
    return [general, ...named];
}
/** This browser's localStorage, or null where there is none or it refuses access (privacy modes, sandboxes). */
function groupStorage() {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }
    catch {
        return null;
    }
}
/** The groups the person collapsed or opened, as this browser remembers them. Never throws; nothing remembered is {}. */
export function readCollapsedGroups(storage = groupStorage()) {
    try {
        const raw = JSON.parse(storage?.getItem(MASCOT_GROUPS_STORAGE_KEY) ?? '{}');
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            return {};
        const chosen = {};
        for (const [id, value] of Object.entries(raw)) {
            if (typeof value === 'boolean' && (id === GENERAL_GROUP_ID || CATEGORY_ID.test(id)))
                chosen[id] = value;
        }
        return chosen;
    }
    catch {
        return {};
    }
}
/** Remember the groups the person collapsed or opened. Never throws (a browser that refuses storage forgets at the next open). */
export function writeCollapsedGroups(chosen, storage = groupStorage()) {
    try {
        storage?.setItem(MASCOT_GROUPS_STORAGE_KEY, JSON.stringify(chosen));
    }
    catch {
        /* nothing remembered */
    }
}
/**
 * Whether a group shows collapsed: as the person last left it; else open when it holds the selected tile or today's day
 * outfit (`selectedId`: the pick, or the tile a save is putting there), so the pick is never hidden by default; else
 * collapsed only when it holds more than MASCOT_GROUP_COLLAPSE_OVER outfits.
 */
export function groupCollapsed(group, chosen, selectedId) {
    const stored = chosen[group.id];
    if (stored !== undefined)
        return stored;
    if (groupHoldsSelection(group, selectedId))
        return false;
    return group.tiles.length > MASCOT_GROUP_COLLAPSE_OVER;
}
/** Whether a group holds the selected tile (`selectedId`) or today's day outfit. */
export function groupHoldsSelection(group, selectedId) {
    return group.tiles.some((tile) => tile.id === selectedId || tile.today === true);
}
/**
 * "Collapse all" or "Expand all" (owner, 2026-10-11): every one of `groups` set to `collapsed`, on top of `chosen`, so a
 * group not on screen just now (filtered out) keeps what the person chose for it.
 */
export function allGroupsCollapsed(groups, collapsed, chosen = {}) {
    const next = { ...chosen };
    for (const group of groups)
        next[group.id] = collapsed;
    return next;
}
/** A day outfit whose run lasts at most this long (a day, with room for a zone's daylight-saving change) is "Today only". */
const ONE_DAY_RUN_MS = 26 * 60 * 60_000;
export function mascotLooksModel(view, builtInSrc, options = {}) {
    const day = view.day;
    const tiles = [
        { id: BUILT_IN_MASCOT_LOOK, name: 'Gyro', src: builtInSrc, mirrored: true, featured: false, detail: 'Always available', ...(view.builtInHasActions !== undefined ? { hasActions: view.builtInHasActions } : {}) },
        ...view.outfits.map((outfit) => {
            const today = day?.outfitId === outfit.id ? day : undefined;
            return {
                id: outfit.id,
                name: outfit.name,
                src: outfit.thumbnailPng ? `data:image/png;base64,${outfit.thumbnailPng}` : '',
                mirrored: false,
                featured: outfit.featured,
                detail: today && today.endMs - today.startMs <= ONE_DAY_RUN_MS ? 'Today only' : mascotWindowEnd(outfit.window, options),
                ...(today ? { today: true } : {}),
                ...(outfit.category ? { category: outfit.category } : {}),
                sha256: outfit.sha256,
                ...(outfit.rating ? { rating: outfit.rating } : {}),
                ...(outfit.addedAtMs !== undefined ? { addedAtMs: outfit.addedAtMs } : {}),
                ...(outfit.picks !== undefined ? { picks: outfit.picks } : {}),
                ...(outfit.dayOutfit ? { dayOutfit: true } : {}),
                ...(outfit.hasActions !== undefined ? { hasActions: outfit.hasActions } : {}),
                ...(outfit.economy ? { economy: outfit.economy } : {}),
            };
        }),
    ];
    const pickGone = view.pick !== null && view.pick !== BUILT_IN_MASCOT_LOOK && !view.outfits.some((outfit) => outfit.id === view.pick);
    const note = pickGone ? 'The outfit you picked is no longer available.'
        : view.pick === null && view.featuredId !== null && view.showing === view.featuredId ? 'IT’s featured outfit is showing. Pick one to keep it.'
            : '';
    const dayNote = !day ? ''
        : view.pickHeld === true ? `Today: ${day.name}. You’re keeping your own outfit today.`
            : view.showing === day.outfitId ? `Today: ${day.name}. Gyro wears it for the day, then your own outfit comes back.`
                : `Today: ${day.name}.`;
    return {
        tiles,
        selectedId: tiles.some((tile) => tile.id === view.showing) ? view.showing : null,
        note,
        dayNote,
        // Offered only when there is a stored pick to clear. Hidden when that pick is the built-in Gyro and IT features
        // nothing: automatic would then show the very same Gyro, so the button would seem to do nothing.
        offerAutomatic: view.pick !== null && (view.featuredId !== null || view.pick !== BUILT_IN_MASCOT_LOOK),
        noOutfits: view.outfits.length === 0,
    };
}
/** Tell the desktop shell the pick changed (it relays this to the agent, which re-reads the pick itself). */
export function announceMascotLookChanged(target = typeof window === 'undefined' ? undefined : window) {
    target?.dispatchEvent(new CustomEvent(MASCOT_LOOK_CHANGED_EVENT));
}
//# sourceMappingURL=mascotLooks.js.map